import { sql } from "drizzle-orm";
import {
  check,
  foreignKey,
  index,
  integer,
  jsonb,
  pgTable,
  text,
  timestamp,
  uniqueIndex,
  varchar,
} from "drizzle-orm/pg-core";
import { lessons, organisations, practiceTodos, students, users } from "./schemaBase.js";
import type { NeckDiagramSheet } from "./neckDiagrams/model.js";

export const neckDiagrams = pgTable(
  "neck_diagrams",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    organisationId: varchar("organisation_id")
      .notNull()
      .references(() => organisations.id),
    title: text("title").notNull(),
    document: jsonb("document").$type<NeckDiagramSheet>().notNull(),
    authorUserId: varchar("author_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    originNeckDiagramId: varchar("origin_neck_diagram_id"),
    version: integer("version").notNull().default(1),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    deletedAt: timestamp("deleted_at", { withTimezone: true }),
  },
  (t) => [
    foreignKey({
      columns: [t.originNeckDiagramId],
      foreignColumns: [t.id],
      name: "neck_diagrams_origin_fk",
    }).onDelete("set null"),
    check("neck_diagrams_title_check", sql`length(trim(${t.title})) > 0`),
    check("neck_diagrams_version_check", sql`${t.version} > 0`),
    check(
      "neck_diagrams_origin_check",
      sql`${t.originNeckDiagramId} is null or ${t.originNeckDiagramId} <> ${t.id}`,
    ),
    index("neck_diagrams_author_idx").on(
      t.organisationId,
      t.authorUserId,
      t.updatedAt,
    ),
  ],
);
export const lessonNeckDiagrams = pgTable(
  "lesson_neck_diagrams",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    organisationId: varchar("organisation_id")
      .notNull()
      .references(() => organisations.id),
    lessonId: varchar("lesson_id")
      .notNull()
      .references(() => lessons.id, { onDelete: "cascade" }),
    neckDiagramId: varchar("neck_diagram_id")
      .notNull()
      .references(() => neckDiagrams.id, { onDelete: "restrict" }),
    position: integer("position").notNull().default(0),
    snapshotTitle: text("snapshot_title").notNull(),
    snapshotDocument: jsonb("snapshot_document")
      .$type<NeckDiagramSheet>()
      .notNull(),
    token: text("token").notNull(),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => [
    uniqueIndex("lesson_neck_diagrams_pair_idx").on(
      t.lessonId,
      t.neckDiagramId,
    ),
    uniqueIndex("lesson_neck_diagrams_token_idx").on(t.token),
    index("lesson_neck_diagrams_order_idx").on(t.lessonId, t.position),
  ],
);

/** A durable record of what was sent. Withdrawal never deletes the snapshot. */
export const neckDiagramShares = pgTable(
  "neck_diagram_shares",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    organisationId: varchar("organisation_id").notNull().references(() => organisations.id),
    neckDiagramId: varchar("neck_diagram_id").notNull().references(() => neckDiagrams.id, { onDelete: "restrict" }),
    studentId: varchar("student_id").notNull().references(() => students.id, { onDelete: "restrict" }),
    sharedByUserId: varchar("shared_by_user_id").references(() => users.id, { onDelete: "set null" }),
    note: text("note"),
    snapshotTitle: text("snapshot_title").notNull(),
    snapshotDocument: jsonb("snapshot_document").$type<NeckDiagramSheet>().notNull(),
    token: text("token").notNull(),
    sharedAt: timestamp("shared_at", { withTimezone: true }).notNull().defaultNow(),
    revokedAt: timestamp("revoked_at", { withTimezone: true }),
    revokedByUserId: varchar("revoked_by_user_id").references(() => users.id, { onDelete: "set null" }),
  },
  t => [
    uniqueIndex("neck_diagram_shares_live_idx").on(t.neckDiagramId, t.studentId).where(sql`${t.revokedAt} is null`),
    uniqueIndex("neck_diagram_shares_token_idx").on(t.token),
    index("neck_diagram_shares_student_idx").on(t.organisationId, t.studentId, t.sharedAt),
  ],
);

export const neckDiagramAssignments = pgTable(
  "neck_diagram_assignments",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    organisationId: varchar("organisation_id").notNull().references(() => organisations.id),
    neckDiagramShareId: varchar("neck_diagram_share_id").notNull(),
    practiceTodoId: varchar("practice_todo_id").notNull().references(() => practiceTodos.id, { onDelete: "cascade" }),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  },
  t => [
    foreignKey({ columns: [t.neckDiagramShareId], foreignColumns: [neckDiagramShares.id], name: "neck_diagram_assignments_share_fk" }).onDelete("restrict"),
    uniqueIndex("neck_diagram_assignments_share_idx").on(t.neckDiagramShareId),
    uniqueIndex("neck_diagram_assignments_todo_idx").on(t.practiceTodoId),
  ],
);

/** Staff library entries retain the published sheet even after its original changes. */
export const neckDiagramLibraryEntries = pgTable("neck_diagram_library_entries", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  organisationId: varchar("organisation_id").notNull(),
  neckDiagramId: varchar("neck_diagram_id").notNull(),
  publishedByUserId: varchar("published_by_user_id"),
  publishedAt: timestamp("published_at", { withTimezone: true }).notNull().defaultNow(),
  withdrawnAt: timestamp("withdrawn_at", { withTimezone: true }),
  withdrawnByUserId: varchar("withdrawn_by_user_id"),
  snapshotTitle: text("snapshot_title").notNull(),
  snapshotDocument: jsonb("snapshot_document").$type<NeckDiagramSheet>().notNull(),
  category: text("category").$type<"chord" | "scale" | "arpeggio" | "other">().notNull(),
  instrument: text("instrument").$type<NeckDiagramSheet["diagrams"][number]["instrument"]>().notNull(),
}, t => [
  foreignKey({ columns: [t.organisationId], foreignColumns: [organisations.id], name: "neck_diagram_library_organisation_fk" }),
  foreignKey({ columns: [t.neckDiagramId], foreignColumns: [neckDiagrams.id], name: "neck_diagram_library_original_fk" }).onDelete("restrict"),
  foreignKey({ columns: [t.publishedByUserId], foreignColumns: [users.id], name: "neck_diagram_library_publisher_fk" }).onDelete("set null"),
  foreignKey({ columns: [t.withdrawnByUserId], foreignColumns: [users.id], name: "neck_diagram_library_withdrawer_fk" }).onDelete("set null"),
  uniqueIndex("neck_diagram_library_live_idx").on(t.neckDiagramId).where(sql`${t.withdrawnAt} is null`),
  index("neck_diagram_library_filter_idx").on(t.organisationId, t.instrument, t.category, t.publishedAt),
  check("neck_diagram_library_category_check", sql`${t.category} in ('chord', 'scale', 'arpeggio', 'other')`),
]);
